
import { useState } from "react";
import AdminLayout from "@/layouts/AdminLayout";
import { AddBetaTesterForm } from "@/components/user-management/AddBetaTesterForm";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { toast } from "sonner";

export default function ManageBetaTesters() {
  const [refresh, setRefresh] = useState(0);

  const { data: betaTesters, isLoading } = useQuery({
    queryKey: ["beta-testers", refresh],
    queryFn: async () => {
      // Get all subscriptions that are beta testers
      const { data: subscriptions, error: subscriptionError } = await supabase
        .from("subscriptions")
        .select("*, profiles:user_id(name, email)")
        .eq("is_beta_tester", true);

      if (subscriptionError) {
        console.error("Error fetching beta testers:", subscriptionError);
        toast.error("Failed to load beta testers");
        throw subscriptionError;
      }

      return subscriptions;
    }
  });

  const handleRefresh = () => {
    setRefresh(prev => prev + 1);
  };

  return (
    <AdminLayout title="Manage Beta Testers">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="col-span-1">
          <AddBetaTesterForm onSuccess={handleRefresh} />
        </div>
        
        <div className="col-span-1 md:col-span-2">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Beta Testers</CardTitle>
                <Button variant="outline" onClick={handleRefresh}>Refresh</Button>
              </div>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex justify-center p-8">
                  <LoadingSpinner />
                </div>
              ) : betaTesters && betaTesters.length > 0 ? (
                <div className="divide-y">
                  {betaTesters.map((tester: any) => (
                    <div key={tester.id} className="py-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">{tester.profiles?.email || "No email"}</p>
                          <p className="text-sm text-gray-500">{tester.profiles?.name || "No name"}</p>
                        </div>
                        <div className="text-right">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 capitalize">
                            {tester.tier}
                          </span>
                          <p className="text-xs text-gray-500 mt-1">
                            Created: {new Date(tester.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No beta testers found
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
